import {
  AbstractControl,
  FormArray,
  FormControlStatus,
  FormGroup,
  PristineChangeEvent,
  TouchedChangeEvent,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { distinctUntilDeepChanged } from '@pages/office/shared/helpers/distinct-until-deep-changed.helper';
import { OfficeToolsValidators } from '../../office-form/office-form/validators';

export class FormHelper {
  static getControlAllStatusChanges(control: AbstractControl): Observable<FormControlStatus> {
    return control.statusChanges.pipe(startWith(control.status));
  }

  static getControlPendingStatus(control: AbstractControl): Observable<boolean> {
    return this.getControlAllStatusChanges(control).pipe(map(status => status === 'PENDING'));
  }

  static getControlDisabledStatus(control: AbstractControl): Observable<boolean> {
    return this.getControlAllStatusChanges(control).pipe(map(status => status === 'DISABLED'));
  }

  static getControlInvalidStatus(control: AbstractControl): Observable<boolean> {
    return this.getControlAllStatusChanges(control).pipe(map(status => status === 'INVALID'));
  }

  static getControlValidStatus(control: AbstractControl): Observable<boolean> {
    return this.getControlAllStatusChanges(control).pipe(map(status => status === 'VALID'));
  }

  static isTouched(control: AbstractControl): Observable<boolean> {
    return control.events.pipe(
      filter(event => event instanceof TouchedChangeEvent),
      map(event => event.source.touched),
      distinctUntilChanged(),
      startWith(control.touched)
    );
  }

  static isDirty(control: AbstractControl): Observable<boolean> {
    return control.events.pipe(
      filter(event => event instanceof PristineChangeEvent),
      map(event => event.source.dirty),
      distinctUntilChanged(),
      startWith(control.dirty)
    );
  }

  static hasRequiredValidator(control: AbstractControl): boolean {
    return control?.hasValidator(OfficeToolsValidators.required);
  }

  static toggleControlDisabledState(control: AbstractControl, isDisabled: boolean): void {
    isDisabled ? control.enabled && control.disable() : control.disabled && control.enable();
  }

  static toggleControlRequiredValidator(control: AbstractControl, isRequired: boolean): void {
    isRequired
      ? !this.hasRequiredValidator(control) && control.addValidators(OfficeToolsValidators.required)
      : this.hasRequiredValidator(control) && control.removeValidators(OfficeToolsValidators.required);
    control.updateValueAndValidity();
  }

  static markAllControlsTouched(control: AbstractControl): void {
    control && this.traverseAbstractControl(control, this.markAbstractControlTouchedHandler);
  }

  static markAllControlsPristine(control: AbstractControl): void {
    control && this.traverseAbstractControl(control, this.markAbstractControlPristineHandler);
  }

  static getInvalidControlsNames(formGroup: FormGroup | FormArray): string[] {
    return Object.keys(formGroup.controls).flatMap(controlName => {
      const control = formGroup.controls[controlName];
      return control instanceof FormGroup || control instanceof FormArray
        ? FormHelper.getInvalidControlsNames(control)
        : control.invalid
          ? [controlName]
          : [];
    });
  }

  static getRawControlValueChanges(control: AbstractControl): Observable<unknown> {
    return control.valueChanges.pipe(startWith(control.value)).pipe(
      map((controlValue: unknown) => {
        return control instanceof UntypedFormControl ? controlValue : (control as UntypedFormGroup | UntypedFormArray).getRawValue();
      }),
      distinctUntilDeepChanged()
    );
  }

  static setControlErrors(control: AbstractControl, errors: ValidationErrors): void {
    control?.setErrors(errors);
    control?.markAsTouched();
  }

  static clearErrorsByErrorKey(control: AbstractControl, errorKey: string): void {
    if (control?.errors && control.errors[errorKey]) {
      const remainingErrors = { ...control.errors };
      delete remainingErrors[errorKey];
      Object.keys(remainingErrors).length ? control.setErrors(remainingErrors) : control.setErrors(null);
    }
  }

  private static markAbstractControlTouchedHandler(control: AbstractControl): void {
    control.markAsTouched({ onlySelf: true });
  }

  private static markAbstractControlPristineHandler(control: AbstractControl): void {
    control.markAsPristine({ onlySelf: true });
  }

  private static traverseAbstractControl(control: AbstractControl, handler: (control: AbstractControl) => void): void {
    control instanceof FormGroup || control instanceof FormArray
      ? Object.values(control.controls).forEach((childControl: AbstractControl) => this.traverseAbstractControl(childControl, handler))
      : handler(control);
  }
}
